/**
 * Calculate season averages for gymnasts across multiple meets
 */

import { isAzTeam, normalizeTeamForMatching } from './azTeams';

/**
 * Combine gymnasts from multiple meets and calculate averages
 * Only includes Arizona teams for season averages
 * @param {Array} meets - Array of meet objects with parsed results
 * @returns {Object} - Averaged results grouped by level and division
 */
export const calculateSeasonAverages = (meets) => {
  // Collect all gymnasts across all meets
  const gymnastScores = {}; // key: "name-normalizedTeam-level-division", value: array of scores

  meets.forEach((meet) => {
    meet.results.allGymnasts.forEach((gymnast) => {
      // Only include Arizona teams in season averages
      if (!isAzTeam(gymnast.team)) {
        return;
      }

      // Normalize all fields for matching across meets
      // NOTE: We only match on name, team, and level (NOT division)
      // because divisions can vary between meets (e.g., "12-13 yrs" vs "Group 1")
      const normalize = (str) => String(str || '').trim().toLowerCase();
      const normalizedTeam = normalizeTeamForMatching(gymnast.team);
      const key = `${normalize(gymnast.name)}-${normalizedTeam}-${normalize(gymnast.level)}`;

      if (!gymnastScores[key]) {
        gymnastScores[key] = {
          name: gymnast.name,
          team: gymnast.team, // Keep original team name for display
          level: gymnast.level,
          division: gymnast.division, // Use first division seen
          scores: [],
          meetNames: []
        };
      }

      gymnastScores[key].scores.push(gymnast.allAround.score);
      gymnastScores[key].meetNames.push(meet.name);
    });
  });

  // Calculate averages
  const gymnasts = Object.values(gymnastScores).map((g) => {
    const total = g.scores.reduce((sum, score) => sum + score, 0);
    const average = total / g.scores.length;
    const highest = Math.max(...g.scores);
    const lowest = Math.min(...g.scores);

    return {
      name: g.name,
      team: g.team,
      level: g.level,
      division: g.division,
      average,
      highest,
      lowest,
      meetCount: g.scores.length,
      scores: g.scores,
      meetNames: g.meetNames
    };
  });

  // Group by level only (not division, since divisions vary between meets)
  const groups = {};
  gymnasts.forEach((gymnast) => {
    const key = `Level${gymnast.level}`;

    if (!groups[key]) {
      groups[key] = {
        level: gymnast.level,
        division: 'All Ages', // Use generic division label since we're combining all divisions
        athletes: []
      };
    }

    groups[key].athletes.push(gymnast);
  });

  // Get top 10 for each group based on average
  const allStars = {};
  Object.keys(groups).forEach((key) => {
    const group = groups[key];
    const sorted = [...group.athletes].sort((a, b) => b.average - a.average);
    const top10 = sorted.slice(0, 10);

    allStars[key] = {
      level: group.level,
      division: group.division,
      top10,
      totalAthletes: group.athletes.length
    };
  });

  return {
    totalGymnasts: gymnasts.length,
    groups: allStars,
    allGymnasts: gymnasts
  };
};

/**
 * Format score for display
 */
export const formatAverage = (score) => {
  return score.toFixed(3);
};

/**
 * Get readable label for meet count
 */
export const getMeetCountLabel = (count, total) => {
  return `${count}/${total} meets`;
};
